/**
 * Self-contained SHA-256 (FIPS 180-4) plus a small iterated salted hash.
 *
 * Why this file exists: the mobile app runs on Hermes, which ships no WebCrypto
 * `subtle` and no Node builtins. Everything here is plain TypeScript over typed
 * arrays so the exact same code runs in the app and under vitest on Node. No
 * `crypto`, no `Buffer`, no external packages.
 *
 * About `pbkdf2ishHex`: it is NOT PBKDF2 and is not a standards-compliant KDF.
 * It is an iterated salted hash, and it is named "ish" so nobody mistakes it for
 * the real thing. Its only job is to add a fixed CPU cost to each guess in the
 * specific scenario where an attacker has already extracted a stored PIN hash
 * out of the OS secure store (Keychain / Keystore) and is grinding the short
 * numeric PIN space offline. It buys time against that grind; it does not give
 * the memory-hardness, HMAC construction, or output-length flexibility of
 * PBKDF2/scrypt/Argon2. If a vetted native KDF ever becomes available on the
 * platform, prefer it and delete this.
 */

/**
 * Round constants K[0..63]: first 32 bits of the fractional parts of the cube
 * roots of the first 64 primes (FIPS 180-4 section 4.2.2).
 */
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
]);

/**
 * Initial hash values H[0..7]: first 32 bits of the fractional parts of the
 * square roots of the first 8 primes (FIPS 180-4 section 5.3.3).
 */
const H0 = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
]);

/** Rotate a 32-bit word right by `n` bits. */
function rotr(x: number, n: number): number {
    return ((x >>> n) | (x << (32 - n))) >>> 0;
}

// ---------------------------------------------------------------------------
// UTF-8 encoding
// ---------------------------------------------------------------------------

/**
 * Encode a JS string to UTF-8 bytes.
 *
 * `TextEncoder` is used when present (it is on modern Hermes and on Node), but
 * we keep a hand-rolled fallback so this module has no environment assumptions.
 * The fallback follows WHATWG encoding semantics: surrogate pairs combine into
 * one code point, and an unpaired surrogate becomes U+FFFD rather than emitting
 * invalid UTF-8. Both paths therefore agree byte-for-byte.
 */
function utf8Encode(input: string): Uint8Array {
    if (typeof TextEncoder !== "undefined") {
        return new TextEncoder().encode(input);
    }

    const bytes: number[] = [];
    for (let i = 0; i < input.length; i++) {
        let cp = input.charCodeAt(i);

        if (cp >= 0xd800 && cp <= 0xdbff) {
            const low = i + 1 < input.length ? input.charCodeAt(i + 1) : 0;
            if (low >= 0xdc00 && low <= 0xdfff) {
                cp = 0x10000 + ((cp - 0xd800) << 10) + (low - 0xdc00);
                i++;
            } else {
                cp = 0xfffd; // unpaired high surrogate
            }
        } else if (cp >= 0xdc00 && cp <= 0xdfff) {
            cp = 0xfffd; // unpaired low surrogate
        }

        if (cp < 0x80) {
            bytes.push(cp);
        } else if (cp < 0x800) {
            bytes.push(0xc0 | (cp >>> 6), 0x80 | (cp & 0x3f));
        } else if (cp < 0x10000) {
            bytes.push(0xe0 | (cp >>> 12), 0x80 | ((cp >>> 6) & 0x3f), 0x80 | (cp & 0x3f));
        } else {
            bytes.push(
                0xf0 | (cp >>> 18),
                0x80 | ((cp >>> 12) & 0x3f),
                0x80 | ((cp >>> 6) & 0x3f),
                0x80 | (cp & 0x3f)
            );
        }
    }
    return new Uint8Array(bytes);
}

// ---------------------------------------------------------------------------
// Core digest
// ---------------------------------------------------------------------------

/** Reused across blocks and calls; this module is synchronous and single-threaded. */
const W = new Uint32Array(64);

/**
 * SHA-256 over raw bytes, returning the 32-byte digest.
 *
 * Padding (FIPS 180-4 section 5.1.1): append 0x80, then enough zero bytes for
 * the total to land 8 bytes short of a 64-byte multiple, then the message
 * length in *bits* as a 64-bit big-endian integer.
 */
function sha256Bytes(data: Uint8Array): Uint8Array {
    const byteLen = data.length;
    const paddedLen = (((byteLen + 8) >>> 6) + 1) << 6;
    const buf = new Uint8Array(paddedLen);
    buf.set(data);
    buf[byteLen] = 0x80;

    // Bit length as a 64-bit big-endian value. Split before shifting because
    // `byteLen << 3` would overflow 32 bits for inputs at or above 512 MiB.
    const bitsHi = Math.floor(byteLen / 0x20000000);
    const bitsLo = (byteLen << 3) >>> 0;
    for (let i = 0; i < 4; i++) {
        buf[paddedLen - 8 + i] = (bitsHi >>> (24 - i * 8)) & 0xff;
        buf[paddedLen - 4 + i] = (bitsLo >>> (24 - i * 8)) & 0xff;
    }

    const h = new Uint32Array(H0);

    for (let offset = 0; offset < paddedLen; offset += 64) {
        // Message schedule: first 16 words are the block itself, big-endian.
        for (let t = 0; t < 16; t++) {
            const p = offset + t * 4;
            W[t] = ((buf[p] << 24) | (buf[p + 1] << 16) | (buf[p + 2] << 8) | buf[p + 3]) >>> 0;
        }
        // Remaining 48 words are derived with the sigma0/sigma1 mixing functions.
        for (let t = 16; t < 64; t++) {
            const w15 = W[t - 15];
            const w2 = W[t - 2];
            const s0 = (rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3)) >>> 0;
            const s1 = (rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10)) >>> 0;
            W[t] = (W[t - 16] + s0 + W[t - 7] + s1) >>> 0;
        }

        let a = h[0];
        let b = h[1];
        let c = h[2];
        let d = h[3];
        let e = h[4];
        let f = h[5];
        let g = h[6];
        let hh = h[7];

        // 64 compression rounds. Intermediate sums stay well under 2^53, so
        // plain addition is exact and `>>> 0` performs the mod-2^32 wrap.
        for (let t = 0; t < 64; t++) {
            const S1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
            const ch = ((e & f) ^ (~e & g)) >>> 0;
            const temp1 = (hh + S1 + ch + K[t] + W[t]) >>> 0;
            const S0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
            const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
            const temp2 = (S0 + maj) >>> 0;

            hh = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }

        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }

    const out = new Uint8Array(32);
    for (let i = 0; i < 8; i++) {
        out[i * 4] = (h[i] >>> 24) & 0xff;
        out[i * 4 + 1] = (h[i] >>> 16) & 0xff;
        out[i * 4 + 2] = (h[i] >>> 8) & 0xff;
        out[i * 4 + 3] = h[i] & 0xff;
    }
    return out;
}

const HEX = "0123456789abcdef";

function toHex(bytes: Uint8Array): string {
    let s = "";
    for (let i = 0; i < bytes.length; i++) {
        s += HEX[bytes[i] >>> 4] + HEX[bytes[i] & 0x0f];
    }
    return s;
}

/**
 * SHA-256 of a string, UTF-8 encoded, as 64 lowercase hex characters.
 */
export function sha256Hex(input: string): string {
    return toHex(sha256Bytes(utf8Encode(input)));
}

/**
 * Iterated salted hash. NOT PBKDF2 — see the file header.
 *
 * Seeds with `sha256Hex(salt + ":" + input)`, then re-hashes the hex digest
 * `iterations` more times as `h = sha256Hex(h + salt)`. The salt is folded into
 * every round so a precomputed chain cannot be shared across users.
 *
 * `iterations` is the number of rounds *after* the seed, so a count of 0 is
 * simply the seed hash. Cost is linear in `iterations`.
 *
 * Be realistic about how much this buys. Measured here, 10,000 rounds costs
 * about 10 ms per verification in this pure-JS implementation on a laptop JS
 * engine — cheap enough to keep PIN entry instant, but that also means a
 * 4-digit keyspace falls in roughly 100 seconds to an attacker merely re-using
 * this same slow JS code. An attacker who has dumped the secure store will use
 * native or GPU SHA-256 instead, at which point a 4-6 digit PIN is recovered
 * essentially immediately at any iteration count that keeps unlock usable.
 *
 * So: this raises the floor against casual recovery from a backup or forensic
 * dump, and it is strictly better than storing the PIN verbatim. It is NOT a
 * defence against a determined offline attack on a short numeric PIN. What
 * actually bounds that risk is the OS secure store keeping the digest out of
 * reach, plus server-side attempt limits — not this function.
 */
export function pbkdf2ishHex(input: string, salt: string, iterations: number): string {
    if (!Number.isInteger(iterations) || iterations < 0) {
        throw new RangeError(
            `pbkdf2ishHex: iterations must be a non-negative integer, got ${iterations}`
        );
    }

    let h = sha256Hex(salt + ":" + input);
    for (let i = 0; i < iterations; i++) {
        h = sha256Hex(h + salt);
    }
    return h;
}
